<template>
  <Stack class="stk-py-8">
    <template v-if="hasFetchedPermissions">
      <Badge status="info">
        Created on {{ formatDate(entity.created_at) }}
      </Badge>

      <Stack gap="2">
        <Title size="4">Your Permissions</Title>
        <Badges>
          <Badge status="info" v-if="currentUserPermissions.includes('view_entity')">Can View</Badge>
          <Badge status="success" v-if="currentUserPermissions.includes('change_entity')">Can Edit</Badge>
          <Badge status="danger" v-if="!currentUserPermissions.length">No Permissions</Badge>
        </Badges>
      </Stack>

      <Stack gap="2" v-if="isEditable">
        <Title size="4">Manage Permissions</Title>
        <table class="table is-fullwidth is-bordered mb-0">
          <thead>
            <tr>
              <th>Email</th>
              <th>Can View</th>
              <th>Can Edit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in entity.permissions" :key="user.id">
              <td>{{ user.email }}</td>
              <td>
                <input 
                  type="checkbox" 
                  :checked="user.permissions.includes('view_entity')"
                  @change="updatePermission(user.email, 'view_entity', $event.target.checked)"
                >
              </td>
              <td>
                <input 
                  type="checkbox" 
                  :checked="user.permissions.includes('change_entity')"
                  @change="updatePermission(user.email, 'change_entity', $event.target.checked)"
                >
              </td>
              <td>
              </td>
            </tr>

            <!-- Add New User Row -->
            <tr>
              <td>
                <div class="stk-select stk-size-3_5 is-fullwidth">
                  <select 
                    v-model="newUserEmail" 
                    @focus="loadUserOptions"
                  >
                    <option value="">Select user...</option>
                    <option 
                      v-for="email in filteredUsers" 
                      :key="email" 
                      :value="email"
                    >
                      {{ email }}
                    </option>
                  </select>
                  <Icon />
                </div>
              </td>
              <td>
                <input type="checkbox" v-model="newUserCanView">
              </td>
              <td>
                <input type="checkbox" v-model="newUserCanEdit">
              </td>
              <td>
                <Button
                  variant="primary"
                  size="3_5"
                  @click="addPermission" 
                  :disabled="!newUserEmail"
                >
                  Add
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </Stack>
    </template>

    <LoaderWithText v-else>
      Fetching permissions…
    </LoaderWithText>
  </Stack>
</template>

<script>
import * as Sentry from '@sentry/vue';
import {
  Badge,
  Badges,
  Button,
  Callout,
  Icon,
  LoaderWithText,
  Stack,
  Title,
} from '@stakeui';

export default {
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      required: true,
    },
  },
  components: {
    Badge,
    Badges,
    Button,
    Callout,
    Icon,
    LoaderWithText,
    Stack,
    Title,
  },
  data() {
    return {
      currentUserPermissions: [],
      hasFetchedPermissions: false,
      isEditable: false,
      newUserEmail: '',
      newUserCanView: true,
      newUserCanEdit: false,
      filteredUsers: [],
    };
  },
  created() {
    this.fetchPermissions();
  },
  methods: {
    async fetchPermissions() {
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/permissions/`);
        if (!response.ok) {
          this.isEditable = false;
          this.currentUserPermissions = [];
          return;
        }
        
        const data = await response.json();
        this.entity.permissions = data.permissions || [];
        this.currentUserPermissions = data.current_user_permissions.permissions || [];
        this.isEditable = this.currentUserPermissions.includes('change_entity');
      } catch (error) {
        this.isEditable = false;
        this.currentUserPermissions = [];
        this.$buefy.toast.open({
          message: 'Failed to fetch permissions',
          type: 'is-danger',
        });
        Sentry.captureMessage('Error fetching permissions in EntityPermissions', { level: 'warning' });
      } finally {
        this.hasFetchedPermissions = true;
      }
    },
    async loadUserOptions() {
      try {
        const response = await fetch('/api/v1/users/share_permissions/');
        const { request_user, permission_users } = await response.json();
        this.filteredUsers = permission_users.filter(user => user !== request_user);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load users',
          type: 'is-danger'
        });
        Sentry.captureMessage('Error loading user options in EntityPermissions', { level: 'warning' });
      }
    },
    async updatePermission(userEmail, permission, value) {
      console.log('THIS LOADING')
      console.log(this.loading)
      if (this.loading) return;
      this.loading = true;

      try {
        const action = value ? 'add' : 'remove';
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/permissions/${action}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            email: userEmail,
            permission: permission,
          })
        });
        console.log('RESPONSE HERE UPDATE')
        console.log(response)

        if (!response.ok) {
          throw new Error('Failed to update permission');
        }

        await this.fetchPermissions();
        
        this.$buefy.toast.open({
          message: 'Permissions updated successfully',
          type: 'is-success'
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to update permission',
          type: 'is-danger'
        });
        Sentry.captureMessage('Error updating permission in EntityPermissions', { level: 'warning' });
      } finally {
        this.loading = false;
      }
    },
    async addPermission() {
      if (!this.newUserEmail || this.loading) return;
      try {
        const permissions = [];
        if (this.newUserCanView) permissions.push('view_entity');
        if (this.newUserCanEdit) permissions.push('change_entity');

        for (const permission of permissions) {
          console.log('CALLING UPDATE')
          await this.updatePermission(this.newUserEmail, permission, true);
        }

        // Reset form
        this.newUserEmail = '';
        this.newUserCanView = true;
        this.newUserCanEdit = false;

      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error adding permissions',
          type: 'is-danger'
        });
        Sentry.captureMessage('Error adding permission in EntityPermissions', { level: 'warning' });
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
  },
};
</script>
